/**
 * Reconciliation Panel
 * ====================
 * Main reconciliation view for drilling down into P&L cell transactions.
 *
 * Layout:
 * - Left sidebar: Expected amount, variance, status, anomalies, progress
 * - Main area: Transaction table with search/filters
 * - Modals: Transaction detail, PDF viewer, Sign-off
 * 
 * P3 Features:
 * - Real-time collaboration with presence indicators
 * - Mobile responsive layout
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import TransactionTable from './TransactionTable';
import TransactionDetailModal from './TransactionDetailModal';
import SignOffModal from './SignOffModal';
import PDFViewerModal from './PDFViewerModal';
import PatternStatsDashboard from './PatternStatsDashboard';
import { AlertTriangle, CheckCircle, Clock, Users, BarChart3 } from 'lucide-react';
import { useCollaboration, PresenceIndicator } from '../../hooks/useCollaboration';

interface ReconciliationPanelProps {
  reportId: string;
  year: number;
  month: number;
  section: string;
  lineItem: string;
  onBack: () => void;
  userId?: string;
  userName?: string;
  eventId?: string;
}

interface Transaction {
  id: string;
  date: string;
  vendor: string;
  description: string;
  amount: number;
  source_file: string;
  source_page: number;
  category: string;
  subcategory?: string;
  original_category?: string;
  is_reclassified: boolean;
  reclassification_reason?: string;
  checked: boolean;
  flagged: boolean;
  flag_reason?: string;
  needs_review: boolean;
}

interface CellData {
  cell_id: string;
  section: string;
  line_item: string;
  year: number;
  month: number;
  expected_amount: number;
  transactions: Transaction[];
  summary: {
    total_transactions: number;
    total_amount: number;
    variance: number;
    variance_pct: number;
    reviewed_count: number;
    flagged_count: number;
    reclassified_count: number;
  };
  anomalies: any[];
  reconciliation_status?: string;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);
};

export default function ReconciliationPanel({
  reportId,
  year,
  month,
  section,
  lineItem,
  onBack,
  userId = 'anonymous',
  userName = 'Anonymous User',
  eventId
}: ReconciliationPanelProps) {
  const [cellData, setCellData] = useState<CellData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal states
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showSignOffModal, setShowSignOffModal] = useState(false);
  const [showStatsDashboard, setShowStatsDashboard] = useState(false);
  
  // PDF Viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfViewerFile, setPdfViewerFile] = useState('');
  const [pdfViewerPage, setPdfViewerPage] = useState(1);
  const [pdfViewerContext, setPdfViewerContext] = useState<{date: string; vendor: string; amount: number} | undefined>(undefined);

  // Real-time collaboration (P3 Feature)
  const handleTransactionUpdateFromWS = useCallback((update: any) => {
    // Refresh data when another user makes changes
    if (update.userId !== userId) {
      console.log('📡 Received update from:', update.userName, update.type);
      fetchCellData();
    }
  }, [userId]);

  const { isConnected, activeUsers, sendUpdate } = useCollaboration({
    reportId,
    userId,
    userName,
    onTransactionUpdate: handleTransactionUpdateFromWS,
    enabled: true
  });

  useEffect(() => {
    fetchCellData();
  }, [reportId, year, month, section, lineItem]);

  const fetchCellData = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        year: year.toString(),
        month: month.toString(),
        section: section,
        line_item: lineItem
      });

      // api.get returns data directly, not wrapped in response object
      const data = await api.get(`/api/v1/reconciliation/cell-transactions/${reportId}?${params.toString()}`);
      console.log('Cell transactions response:', data);
      setCellData(data);
    } catch (err: any) {
      console.error('Error fetching cell transactions:', err);
      setError(err.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleTransactionClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
  };

  const handleTransactionUpdate = () => {
    // Refresh data after transaction is updated
    fetchCellData();
  };

  const handleSignOff = () => {
    setShowSignOffModal(true);
  };

  const handleSignOffComplete = () => {
    setShowSignOffModal(false);
    fetchCellData(); // Refresh to show new status
  };

  // Handle viewing source document
  const handleViewSourceDocument = (sourceFile: string, sourcePage: number, transaction: Transaction) => {
    setPdfViewerFile(sourceFile);
    setPdfViewerPage(sourcePage);
    setPdfViewerContext({
      date: transaction.date,
      vendor: transaction.vendor,
      amount: transaction.amount
    });
    setPdfViewerOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading transactions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-red-800 font-bold text-lg mb-2">Error</h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={onBack}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Back to Grid
        </button>
      </div>
    );
  }

  if (!cellData) {
    return null;
  }

  const { summary, transactions, expected_amount, anomalies, reconciliation_status } = cellData;
  const varianceClass = Math.abs(summary.variance) < 1 ? 'text-gray-600' : summary.variance > 0 ? 'text-red-600' : 'text-green-600';
  const progressPercent = Math.round((summary.reviewed_count / summary.total_transactions) * 100);

  return (
    <div className="space-y-4">
      {/* Real-time Collaboration Header (P3 Feature) */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white rounded-lg shadow px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-blue-600 hover:text-blue-800 font-medium text-sm"
          >
            ← Back to Grid
          </button>
          <span className="text-gray-300">|</span>
          <span className="text-sm text-gray-600">
            {section} &gt; <span className="font-medium">{lineItem}</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Connection Status */}
          <div className={`flex items-center gap-1.5 text-xs ${isConnected ? 'text-green-600' : 'text-gray-400'}`}>
            <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-400'}`}></span>
            {isConnected ? 'Live' : 'Offline'}
          </div>
          {/* Presence Indicator */}
          <PresenceIndicator activeUsers={activeUsers} currentUserId={userId} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
      {/* Left Sidebar */}
      <div className="lg:col-span-1 space-y-4">
        {/* Expected Amount */}
        <div className="bg-white rounded-lg shadow p-4 lg:p-5">
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Expected P&L Amount</div>
          <div className="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">{formatCurrency(expected_amount)}</div>
          <div className="text-sm text-gray-600 truncate" title={lineItem}>{lineItem}</div>
        </div>

        {/* Transaction Summary */}
        <div className="bg-white rounded-lg shadow p-4 lg:p-5">
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Transaction Summary</div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Count:</span>
              <span className="text-sm font-semibold text-gray-900">{summary.total_transactions}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Sum:</span>
              <span className="text-sm font-semibold text-gray-900">{formatCurrency(summary.total_amount)}</span>
            </div>
            <div className="border-t pt-3">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-bold text-gray-500 uppercase">Variance:</span>
              </div>
              <div className="flex justify-between items-center">
                <span className={`text-xl lg:text-2xl font-bold ${varianceClass}`}>{formatCurrency(summary.variance)}</span>
                <span className="text-sm text-gray-500">({summary.variance_pct.toFixed(1)}%)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Verification Status */}
        <div className="bg-white rounded-lg shadow p-5">
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Verification Status</div>
          <div className="space-y-2 mb-4">
            {reconciliation_status === 'reconciled' ? (
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded">
                <CheckCircle size={16} />
                <span className="font-medium">Reconciled</span>
              </div>
            ) : reconciliation_status === 'in_review' ? (
              <div className="flex items-center gap-2 text-sm text-yellow-700 bg-yellow-50 px-3 py-2 rounded">
                <Clock size={16} />
                <span className="font-medium">In Review</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded">
                <Clock size={16} />
                <span className="font-medium">Not Started</span>
              </div>
            )}
          </div>
          <button
            onClick={handleSignOff}
            className="w-full px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            ✍️ Sign Off
          </button>
        </div>

        {/* Anomalies */}
        {(summary.flagged_count > 0 || summary.reclassified_count > 0 || anomalies.length > 0) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm font-bold text-yellow-900 mb-2">
              <AlertTriangle size={16} />
              Anomalies Detected
            </div>
            <ul className="space-y-1 text-sm text-yellow-800">
              {summary.flagged_count > 0 && (
                <li>• {summary.flagged_count} flagged transaction{summary.flagged_count > 1 ? 's' : ''}</li>
              )}
              {summary.reclassified_count > 0 && (
                <li>• {summary.reclassified_count} reclassified transaction{summary.reclassified_count > 1 ? 's' : ''}</li>
              )}
              {anomalies.map((anomaly, idx) => (
                <li key={idx}>• {anomaly.reason || 'Anomaly detected'}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Review Progress */}
        <div className="bg-white rounded-lg shadow p-5">
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Review Progress</div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Reviewed:</span>
              <span className="font-semibold text-green-600">{summary.reviewed_count}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Flagged:</span>
              <span className="font-semibold text-yellow-600">{summary.flagged_count}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total:</span>
              <span className="font-semibold text-gray-900">{summary.total_transactions}</span>
            </div>
          </div>
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
            <div className="text-center text-xs text-gray-500 mt-1">{progressPercent}% complete</div>
          </div>
        </div>

        {/* Pattern Analytics Button */}
        <div className="bg-white rounded-lg shadow p-4">
          <button
            onClick={() => setShowStatsDashboard(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-50 text-indigo-700 font-medium rounded-lg hover:bg-indigo-100 transition-colors"
            data-testid="view-pattern-stats-btn"
          >
            <BarChart3 size={18} />
            Pattern Analytics
          </button>
        </div>
      </div>

      {/* Main Content - Transaction Table */}
      <div className="lg:col-span-3">
        <TransactionTable
          transactions={transactions}
          expectedAmount={expected_amount}
          reportId={reportId}
          eventId={eventId}
          onTransactionClick={handleTransactionClick}
          onTransactionsUpdate={handleTransactionUpdate}
          onViewSourceDocument={handleViewSourceDocument}
        />
      </div>
      </div>

      {/* Modals */}
      {selectedTransaction && (
        <TransactionDetailModal
          transaction={selectedTransaction}
          reportId={reportId}
          onClose={() => setSelectedTransaction(null)}
          onUpdate={handleTransactionUpdate}
        />
      )}

      {showSignOffModal && (
        <SignOffModal
          reportId={reportId}
          cellId={cellData.cell_id}
          expectedAmount={expected_amount}
          actualAmount={summary.total_amount}
          variance={summary.variance}
          reclassifiedCount={summary.reclassified_count}
          onClose={() => setShowSignOffModal(false)}
          onComplete={handleSignOffComplete}
        />
      )}

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfViewerOpen}
        reportId={reportId}
        sourceFile={pdfViewerFile}
        sourcePage={pdfViewerPage}
        transactionContext={pdfViewerContext}
        onClose={() => setPdfViewerOpen(false)}
      />

      {/* Pattern Statistics Dashboard Modal */}
      {showStatsDashboard && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <PatternStatsDashboard
              eventId="smitty_supply_2025"
              onClose={() => setShowStatsDashboard(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
